import React, { useEffect, useState } from 'react';
import { reportsHubAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Download, FileSpreadsheet, FileText, Eye, Loader2, Table2 } from 'lucide-react';

const downloadBlob = (data, filename, type) => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

const MIME = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};

export default function ReportsHub() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const [downloading, setDownloading] = useState({});
  const [preview, setPreview] = useState(null); // { key, label, filters }
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    reportsHubAPI.catalog()
      .then((r) => setCatalog(r.data.reports || []))
      .catch((e) => toast.error(getErrorMessage(e, 'Failed to load reports')))
      .finally(() => setLoading(false));
  }, []);

  const openPreview = async (key, label, filters = {}) => {
    setPreview({ key, label, filters });
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const r = await reportsHubAPI.preview(key, { ...filters, limit: 50 });
      setPreviewData(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load preview'));
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runExport = async (key, label, format, filters = {}) => {
    const dlKey = `${key}-${format}`;
    setDownloading((p) => ({ ...p, [dlKey]: true }));
    try {
      const r = await reportsHubAPI.exportReport(key, format, filters);
      downloadBlob(r.data, `${key}_report.${format}`, MIME[format]);
      toast.success(`${label} exported (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Export failed'));
    } finally {
      setDownloading((p) => ({ ...p, [dlKey]: false }));
    }
  };

  const askAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    setAiNote('');
    try {
      const r = await reportsHubAPI.ai(aiPrompt.trim());
      setAiNote(r.data.explanation || '');
      await openPreview(r.data.report_key, r.data.label, r.data.filters || {});
    } catch (e) {
      toast.error(getErrorMessage(e, "Couldn't understand that request"));
    } finally {
      setAiBusy(false);
    }
  };

  const categories = [...new Set(catalog.map((r) => r.category))];

  return (
    <div data-testid="reports-hub">
      {/* AI assistant */}
      <div
        className="rounded-2xl p-5 mb-6 text-white"
        style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#334155 100%)' }}
        data-testid="reports-ai-box"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={18} className="text-amber-300" />
          <h3 className="font-semibold text-base">What would you like to see today?</h3>
        </div>
        <p className="text-xs text-slate-300 mb-3">
          Describe the report in plain words — e.g. “pre-licensing progress this month”, “event ROI”, “declined contracts”.
        </p>
        <div className="flex gap-2">
          <Input
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && askAi()}
            placeholder="Ask for any report…"
            className="bg-white/95 text-slate-900 border-0 h-10"
            data-testid="reports-ai-input"
          />
          <Button onClick={askAi} disabled={aiBusy} className="bg-amber-400 hover:bg-amber-500 text-slate-900 h-10 px-5" data-testid="reports-ai-btn">
            {aiBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} className="mr-1.5" />}
            Generate
          </Button>
        </div>
        {aiNote && <p className="text-[11px] text-amber-200 mt-2" data-testid="reports-ai-note">{aiNote}</p>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-slate-400" /></div>
      ) : (
        categories.map((cat) => (
          <div key={cat} className="mb-6">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">{cat}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {catalog.filter((r) => r.category === cat).map((r) => (
                <Card key={r.key} className="hover:shadow-md transition-shadow" data-testid={`report-card-${r.key}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <Table2 size={16} className="text-slate-400 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-slate-900">{r.label}</h4>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{r.description}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openPreview(r.key, r.label)} data-testid={`report-preview-${r.key}`}>
                        <Eye size={13} className="mr-1" /> Preview
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => runExport(r.key, r.label, 'xlsx')} disabled={downloading[`${r.key}-xlsx`]} data-testid={`report-xlsx-${r.key}`}>
                        {downloading[`${r.key}-xlsx`] ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} className="mr-1" />} Excel
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => runExport(r.key, r.label, 'pdf')} disabled={downloading[`${r.key}-pdf`]} data-testid={`report-pdf-${r.key}`}>
                        {downloading[`${r.key}-pdf`] ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} className="mr-1" />} PDF
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => runExport(r.key, r.label, 'csv')} disabled={downloading[`${r.key}-csv`]} data-testid={`report-csv-${r.key}`}>
                        {downloading[`${r.key}-csv`] ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} className="mr-1" />} CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setPreviewData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="report-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 size={16} className="text-slate-500" /> {preview?.label}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {previewData ? `Showing ${previewData.rows.length} of ${previewData.total} rows` : 'Loading preview…'}
              {preview?.filters && Object.keys(preview.filters).length > 0 && (
                <span className="ml-2">
                  {Object.entries(preview.filters).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="ml-1 text-[10px]">{k}: {v}</Badge>
                  ))}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-2">
            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => runExport(preview.key, preview.label, 'xlsx', preview.filters)} data-testid="preview-export-xlsx">
              <FileSpreadsheet size={13} className="mr-1" /> Excel
            </Button>
            <Button size="sm" className="h-8 text-xs bg-slate-800 hover:bg-slate-900" onClick={() => runExport(preview.key, preview.label, 'pdf', preview.filters)} data-testid="preview-export-pdf">
              <FileText size={13} className="mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => runExport(preview.key, preview.label, 'csv', preview.filters)} data-testid="preview-export-csv">
              <Download size={13} className="mr-1" /> CSV
            </Button>
          </div>

          <div className="overflow-auto border border-slate-200 rounded-lg">
            {previewLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
            ) : previewData && previewData.rows.length > 0 ? (
              <table className="w-full text-xs" data-testid="report-preview-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>{previewData.headers.map((h, i) => (
                    <th key={i} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewData.rows.map((row, ri) => (
                    <tr key={ri} className="hover:bg-slate-50">
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-3 py-1.5 text-slate-700 whitespace-nowrap max-w-[280px] truncate" title={String(cell)}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-14 text-center text-sm text-slate-500" data-testid="report-preview-empty">No data for this report / filters.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
